/**
 * A lista da aba Documentos com os dados dela (E205.F4.T02, ADR-0147 §2.5). Lê o que já existia —
 * nenhuma rota nova: os documentos do caso (`listDocuments`), a ficha da leitura (as páginas e os
 * campos), os documentos de apoio e, da mesa, as peças e as leituras propostas das imagens. Relê
 * quando o caso é relido (`releitura`).
 */
import type { AxiosInstance } from 'axios';
import { useMemo, useState } from 'react';

import { useDocumentosDeApoio } from '@/components/ExameEletronico/useDocumentosDeApoio';
import { ExcluirDocumento } from '@/components/ExcluirDocumento/ExcluirDocumento';
import { RecorteDoDocumento } from '@/components/RecorteDoDocumento/RecorteDoDocumento';
import { useDocumentosDoPainel } from '@/components/MesaUnica/useDocumentoOriginal';
import { FalhaComSaida } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { LeituraDaImagemData } from '@/lib/candidatas_de_imagem';
import type { ExameEletronicoCasoData } from '@/lib/exame_eletronico';
import { arquivosUnicosDoCaso } from '@/lib/exame_eletronico_arquivo_unico';
import { useExcluidosNaMesa } from '@/lib/excluidos_na_mesa';
import { useFichaDaLeitura } from '@/lib/useFichaDaLeitura';
import { useLetrasDaSecao2 } from '@/lib/letras_da_secao_2';

import { linhasDosDocumentos, type LinhaDoDocumento } from './linhasDosDocumentos';
import { ListaDeDocumentos } from './ListaDeDocumentos';

const t = ptBR.documentosDoCaso;
const tipos: Partial<Record<string, string>> = ptBR.exameEletronico.tabelaDePecas.tipoDaPeca;

export interface DocumentosDoCasoProps {
  client: AxiosInstance;
  caseId: string;
  caso: ExameEletronicoCasoData | null;
  /** O caso está sendo verificado: a custódia de quem ainda não tem resultado diz que espera. */
  emProcessamento: boolean;
  concluido: boolean;
  /** As leituras propostas das imagens de documento (da leitura das fotografias da mesa). */
  leituras: readonly LeituraDaImagemData[];
  /** Uma leitura foi confirmada: a mesa rearma o processamento e relê. */
  onConfirmado: () => void;
  /** Abre o envio, no alto da aba. */
  onEnviar: () => void;
}

export function DocumentosDoCaso({
  client,
  caseId,
  caso,
  emProcessamento,
  concluido,
  leituras,
  onConfirmado,
  onEnviar,
}: DocumentosDoCasoProps): JSX.Element {
  const [tentativa, setTentativa] = useState(0);
  // E214.F1 (GUS-314): a linha cujo documento está sendo recortado.
  const [recortando, setRecortando] = useState<LinhaDoDocumento | null>(null);
  // E216.F4 (GUS-317 item 3): a linha cujo arquivo está sendo excluído.
  const [excluindo, setExcluindo] = useState<LinhaDoDocumento | null>(null);
  // O caso relido (documento novo, exame) relê a lista, a ficha e o apoio.
  const releitura = useMemo(() => ({ caso, tentativa }), [caso, tentativa]);
  const lista = useDocumentosDoPainel(client, caseId, releitura);
  const { ficha } = useFichaDaLeitura(client, caseId, caso);
  const apoio = useDocumentosDeApoio(caseId, caso);
  // E216.F3 (GUS-317 item 5): a letra de cada arquivo, a mesma da seção 2 do exame
  const exame = useLetrasDaSecao2(client, caseId, releitura);
  // E216.F4.C03 (GUS-317 P6): a listagem só traz os vivos; os excluídos vêm do registro da exclusão
  const excluidos = useExcluidosNaMesa(client, caseId, releitura);
  if (lista.estado === 'carregando') {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="documentos-lendo">
        {t.lendo}
      </p>
    );
  }
  if (lista.estado === 'erro') {
    return (
      <FalhaComSaida
        mensagem={t.erro}
        onTentarDeNovo={() => setTentativa((n) => n + 1)}
        data-testid="documentos-falha"
      />
    );
  }
  const linhas = linhasDosDocumentos({
    documentos: lista.todos,
    ficha: ficha.estado === 'pronto' ? ficha.dados.ficha.ficha : null,
    pecas: caso?.pecas ?? [],
    emProcessamento,
    apoio: apoio.documentos,
    leituras,
    letras: exame.letras,
  });
  const faltantes = (caso?.kit_pendente ?? []).map((tipo) => tipos[tipo] ?? tipo);
  const arquivosComFalha = arquivosUnicosDoCaso(caso).filter(
    (a) => a.separacao.situacao !== 'conferida',
  );
  const reler = (): void => {
    apoio.reler();
    onConfirmado();
  };
  return (
    <>
      <ListaDeDocumentos
        caseId={caseId}
        linhas={linhas}
        faltantes={faltantes}
        arquivosComFalha={arquivosComFalha}
        excluidos={excluidos}
        exameComposto={exame.composto}
        concluido={concluido}
        onConfirmado={reler}
        onEnviar={onEnviar}
        onRecortar={setRecortando}
        onExcluir={setExcluindo}
      />
      {recortando !== null ? (
        <RecorteDoDocumento
          client={client}
          caseId={caseId}
          documento={{ id: recortando.id, nome: recortando.nome, paginas: recortando.paginas }}
          onFechar={() => setRecortando(null)}
          onRecortado={reler}
        />
      ) : null}
      {excluindo !== null ? (
        <ExcluirDocumento
          client={client}
          caseId={caseId}
          documento={{ id: excluindo.id, nome: excluindo.nome }}
          onFechar={() => setExcluindo(null)}
          onExcluido={() => {
            setTentativa((n) => n + 1);
            reler();
          }}
        />
      ) : null}
    </>
  );
}
